import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../../../Constant';
import { showLoading } from 'react-global-loading';
import { toast, ToastContainer } from 'react-toastify';
import messages from '../../../../assets/Messages.json';
import 'react-toastify/dist/ReactToastify.css';
import HeaderInfo from '../../../../Components/Common/HeaderInfo';
import Remarks2 from './Remarks2';
import Remarks__list from './Remarks__list';
import { markPageAsSaved, PAGE_NAMES, refreshBalanceSheetValidation } from '../../../../Components/BalanceSheet/BalanceSheetHeader';
import { filterRemarksList } from '../../../../Custom/Custom';

function ICTUsageAndEnergyConservation() {
  const navigate = useNavigate();

  // Refs to prevent multiple API calls
  const dataLoadedRef = useRef(false);
  const isLoadingRef = useRef(false);
  const apiCallMadeRef = useRef(false);

  // State for header data
  const [headerData, setHeaderData] = useState({
    despatchSlNo: '',
    permanentSlNo: '',
    companyName: '',
    dslNo: '',
    surveyYear: ''
  });

  // State for form data
  const [formData, setFormData] = useState({
    // ICT Usage data
    computerUsage: '',
    internetUsage: '',
    website: '',
    receiveOrders: '',
    placeOrders: '',
    internetConnection: '',
    lan: '',
    // Energy Conservation data
    electricSaving: '',
    coalSaving: '',
    oilSaving: '',
    gasSaving: ''
  });

  // State for Remarks section
  const [remarksData, setRemarksData] = useState({
    remarksBlock: '',
    remarksItems: '',
    remarks: ''
  });
  const [remarksList, setRemarksList] = useState([]);

  // State for dynamic indicators from API
  const [ictIndicators, setIctIndicators] = useState([]);
  const [ecIndicators, setEcIndicators] = useState([]);

  // Authentication configuration
  const getAuthConfig = () => {
    let token = localStorage.getItem('token') ||
      localStorage.getItem('jwt') ||
      localStorage.getItem('accessToken') ||
      localStorage.getItem('authToken') ||
      localStorage.getItem('userToken');

    if (!token) {
      token = sessionStorage.getItem('token') ||
        sessionStorage.getItem('jwt') ||
        sessionStorage.getItem('accessToken');
    }

    if (!token) {
      return null;
    }

    return {
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
        'Cache-Control': 'no-cache'
      }
    };
  };

  // Function to fetch existing data from API
  const fetchExistingData = async (dslNo, surveyYear) => {
    if (!dslNo || !surveyYear) {
      dataLoadedRef.current = true;
      return;
    }

    showLoading(true);
    
    try {
      const currentConfig = getAuthConfig();
      if (!currentConfig) {
        showLoading(false);
        dataLoadedRef.current = true;
        return;
      }

      const payload = {
        dsl_no: dslNo,
        survey_year: surveyYear
      };

      const response = await axios.post(`${API_URL}api/block/kl`, payload, currentConfig);
      
      if (response.data && response.data.status && response.data.data) {
        
        // Map API data to form data based on blockType and itemName
        const apiData = response.data.data;
        
        // Separate Block K and Block L data
        const blockKData = apiData.filter(item => item.blockType === 'ASI_BLOCKK');
        const blockLData = apiData.filter(item => item.blockType === 'ASI_BLOCKL');

        // Create dynamic ICT indicators from API response
        const dynamicIctIndicators = blockKData.map((item, index) => {
          // Map itemName to field id based on keywords
          const itemName = (item.itemName || '').toLowerCase();
          let fieldId = '';
          
          if (itemName.includes('computer')) {
            fieldId = 'computerUsage';
          } else if (itemName.includes('internet') && itemName.includes('use')) {
            fieldId = 'internetUsage';
          } else if (itemName.includes('website')) {
            fieldId = 'website';
          } else if (itemName.includes('receive') && itemName.includes('order')) {
            fieldId = 'receiveOrders';
          } else if (itemName.includes('place') && itemName.includes('order')) {
            fieldId = 'placeOrders';
          } else if (itemName.includes('connect') && itemName.includes('internet')) {
            fieldId = 'internetConnection';
          } else if (itemName.includes('local area network') || itemName.includes('lan')) {
            fieldId = 'lan';
          } else {
            // If no match, create a unique field id based on itemCode
            fieldId = `ict_${item.itemCode || index}`;
          }

          return {
            id: fieldId,
            itemCode: item.itemCode?.toString() || '',
            question: item.itemName || '',
            value: item.value || ''
          };
        });

        // Create dynamic EC indicators from API response
        const dynamicEcIndicators = blockLData.map((item, index) => {
          // Map itemName to field id based on keywords
          const itemName = (item.itemName || '').toLowerCase();
          let fieldId = '';
          
          if (itemName.includes('electrical saving') || itemName.includes('electric saving')) {
            fieldId = 'electricSaving';
          } else if (itemName.includes('coal saving')) {
            fieldId = 'coalSaving';
          } else if (itemName.includes('oil saving')) {
            fieldId = 'oilSaving';
          } else if (itemName.includes('gas saving')) {
            fieldId = 'gasSaving';
          } else {
            // If no match, create a unique field id based on itemCode
            fieldId = `ec_${item.itemCode || index}`;
          }

          return {
            id: fieldId,
            itemCode: item.itemCode?.toString() || '',
            question: item.itemName || '',
            value: item.value || ''
          };
        });

        // Set dynamic indicators
        setIctIndicators(dynamicIctIndicators);
        setEcIndicators(dynamicEcIndicators);

        // Initialize form data dynamically based on API response
        const newFormData = {};
        
        // Add all ICT fields - convert "0" to "" (Select), "1" to "1" (Yes)
        dynamicIctIndicators.forEach(indicator => {
          const apiValue = indicator.value || '';
          // If value is "0" or empty, set to "" (Select)
          // If value is "1", set to "1" (Yes)
          // If value is "2", set to "2" (No)
          newFormData[indicator.id] = (apiValue === '0' || apiValue === '') ? '' : apiValue;
        });
        
        // Add all EC fields - convert "0" to "" (Select), "1" to "1" (Yes)
        dynamicEcIndicators.forEach(indicator => {
          const apiValue = indicator.value || '';
          // If value is "0" or empty, set to "" (Select)
          // If value is "1", set to "1" (Yes)
          // If value is "2", set to "2" (No)
          newFormData[indicator.id] = (apiValue === '0' || apiValue === '') ? '' : apiValue;
        });
        
        // Update form data with mapped values
        setFormData(newFormData);

        // Map remarks data from API response
        let mappedRemarksList = [];
        if (response.data.remarks && Array.isArray(response.data.remarks)) {
          mappedRemarksList = response.data.remarks.map((remark, index) => {
            // Extract block letter from "BLOCK K" or "BLOCK L" format
            let blockLetter = '';
            if (remark.block) {
              // Handle formats like "BLOCK K", "BLOCK-L", "K", etc.
              blockLetter = remark.block.replace(/^BLOCK\s*[-]?\s*/i, '').trim().toUpperCase();
            }

            // Format date for display
            let formattedDate = '';
            const dateValue = remark.createdDate || remark.created_date || remark.updatedDate || remark.updated_date || '';
            if (dateValue) {
              try {
                const date = new Date(dateValue);
                if (!isNaN(date.getTime())) {
                  formattedDate = date.toLocaleString('en-IN', {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  });
                }
              } catch (e) {
                formattedDate = dateValue;
              }
            }

            return {
              slNo: index + 1,
              blocks: blockLetter || remark.block || '',
              columns: remark.columnsName || remark.columns_name || remark.columns || '',
              items: remark.items || remark.Items || '',
              remarks: remark.remarks || '',
              remarksCreatedOn: formattedDate || dateValue || '',
              remarksBy: remark.updatedBy || remark.updated_by || remark.createdBy || remark.created_by || '',
              role: remark.updatedBy || remark.updated_by || remark.createdBy || remark.created_by || ''
            };
          });
          setRemarksList(mappedRemarksList);
          console.log('Mapped remarks list:', mappedRemarksList);
        } else {
          setRemarksList([]);
        }

        // Set default remarksData - Block to "ALL" by default to show all remarks
        setRemarksData({
          remarksBlock: (mappedRemarksList.length > 0) ? 'ALL' : 'SELECT_ITEM',
          remarksItems: '',
          remarks: ''
        });
        
        dataLoadedRef.current = true;
      } else {
        dataLoadedRef.current = true;
      }
    } catch (error) {
      if (error.response?.status === 404) {
        // No existing data found - this is normal
      } else {
        toast.error('Failed to load existing data. Using default form.', messages.COLOR_RED);
      }
      dataLoadedRef.current = true;
    } finally {
      showLoading(false);
    }
  };

  // Fetch header data from localStorage when component mounts
  useEffect(() => {
    // Prevent multiple API calls
    if (dataLoadedRef.current || isLoadingRef.current || apiCallMadeRef.current) {
      return;
    }

    const fetchHeaderData = () => {
      try {
        const balanceSheetData = localStorage.getItem('balanceSheetFormData');

        if (balanceSheetData) {
          const parsedData = JSON.parse(balanceSheetData);

          const headerInfo = {
            despatchSlNo: parsedData.dslNo || '',
            permanentSlNo: parsedData.pslNo || '',
            companyName: parsedData.nameUnit || '',
            dslNo: parsedData.dslNo || '',
            surveyYear: parsedData.year || '2022'
          };

          setHeaderData(headerInfo);
          
          // Fetch existing data if DSL No and Survey Year are available
          if (headerInfo.dslNo && headerInfo.surveyYear) {
            apiCallMadeRef.current = true;
            isLoadingRef.current = true;
            fetchExistingData(headerInfo.dslNo, headerInfo.surveyYear);
            isLoadingRef.current = false;
          }
        } else {
          setHeaderData({
            despatchSlNo: 'DSL No. not set',
            permanentSlNo: 'PSL No. not set',
            companyName: 'Company name not set',
            dslNo: '',
            surveyYear: ''
          });
        }
      } catch (error) {
        setHeaderData({
          despatchSlNo: 'Error loading data',
          permanentSlNo: 'Error loading data',
          companyName: 'Error loading data',
          dslNo: '',
          surveyYear: ''
        });
      }
    };

    fetchHeaderData();
  }, []);

  // Handle input change
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle reset function
  const handleReset = () => {
    const resetData = {};
    // Reset all ICT fields
    ictIndicators.forEach(indicator => {
      resetData[indicator.id] = '';
    });
    // Reset all EC fields
    ecIndicators.forEach(indicator => {
      resetData[indicator.id] = '';
    });
    setFormData(resetData);
  };

  // Function to save data to API
  const handleSaveToAPI = async () => {
    if (!headerData.dslNo || headerData.dslNo === 'DSL No. not set') {
      toast.error('Please ensure DSL No. is available', messages.COLOR_RED);
      return false;
    }

    showLoading(true);

    try {
      const currentConfig = getAuthConfig();
      if (!currentConfig) {
        toast.error('Authentication token not found. Please login again.', messages.COLOR_RED);
        showLoading(false);
        return false;
      }

      // Prepare data for API in the required format
      const asiBlockK = [];
      const asiBlockL = [];

      // Extract year only from survey year (e.g., "2024-25" -> 2024)
      const surveyYearOnly = headerData.surveyYear ? parseInt(headerData.surveyYear.split('-')[0]) : parseInt(headerData.surveyYear || '2024');
      const dslNo = parseInt(headerData.dslNo);
      
      // Add ICT Usage data to ASI_BLOCKK array - dynamically from ictIndicators
      ictIndicators.forEach(indicator => {
        const value = formData[indicator.id];
        if (value && value !== '') {
          asiBlockK.push({
            dslNo: dslNo,
            surveyYear: surveyYearOnly,
            itemCode: parseInt(indicator.itemCode) || 0,
            value: value.toString()
          });
        }
      });
      
      // Add Energy Conservation data to ASI_BLOCKL array - dynamically from ecIndicators
      ecIndicators.forEach(indicator => {
        const value = formData[indicator.id];
        if (value && value !== '') {
          asiBlockL.push({
            dslNo: dslNo,
            surveyYear: surveyYearOnly,
            itemCode: parseInt(indicator.itemCode) || 0,
            value: value.toString()
          });
        }
      });

      // Prepare remarks array
      const remarksArray = [];
      if (remarksData.remarksBlock || remarksData.remarksItems || remarksData.remarks) {
        // Extract block letter from remarksBlock (e.g., "Block K" -> "K", "Block L" -> "L")
        let blockLetter = '';
        if (remarksData.remarksBlock && remarksData.remarksBlock !== 'ALL') {
          // Handle formats like "Block K", "Block L", "K", "L", etc.
          const blockMatch = remarksData.remarksBlock.match(/Block\s*([KL])/i);
          if (blockMatch) {
            blockLetter = blockMatch[1].toUpperCase();
          } else if (remarksData.remarksBlock === 'K' || remarksData.remarksBlock === 'L') {
            blockLetter = remarksData.remarksBlock.toUpperCase();
          }
        }

        remarksArray.push({
          dslNo: headerData.dslNo.toString(),
          surveyYear: surveyYearOnly.toString(),
          block: blockLetter || '',
          columnsName: remarksData.remarksItems || '',
          items: remarksData.remarksBlock && remarksData.remarksBlock !== 'ALL' ? remarksData.remarksBlock : '',
          remarks: remarksData.remarks || ''
        });
      }

      // Structure payload with ASI_BLOCKK, ASI_BLOCKL, and remarks arrays
      const finalPayload = {
        ASI_BLOCKK: asiBlockK,
        ASI_BLOCKL: asiBlockL,
        remarks: remarksArray
      };

      if (asiBlockK.length === 0 && asiBlockL.length === 0) {
        toast.warning('No data to save. Please fill at least one field.', messages.COLOR_RED);
        showLoading(false);
        return false;
      }

      console.log('Saving data to API with payload:', finalPayload);

      const response = await axios.post(`${API_URL}api/block/kl/insert`, finalPayload, currentConfig);
      
      if (response.data && response.data.status) {
        toast.success('Data saved successfully!', messages.COLOR_RED);

        // Mark page as saved for green header
        markPageAsSaved(PAGE_NAMES.ICT_ENERGY);
        // Refresh balance sheet validation to update header colors
        if (headerData.despatchSlNo && headerData.surveyYear) {
          await refreshBalanceSheetValidation(headerData.despatchSlNo, headerData.surveyYear);
        }

        return true;
      } else {
        toast.error('Failed to save data. Please check the data and try again.', messages.COLOR_RED);
        return false;
      }

    } catch (error) {

      if (error.response?.status === 403) {
        toast.error('Access denied (403). Please check your permissions or contact administrator.', messages.COLOR_RED);
      } else if (error.response?.status === 401) {
        toast.error('Unauthorized (401). Please login again.', messages.COLOR_RED);
      } else if (error.response?.status === 404) {
        toast.error('API endpoint not found (404). Please check the URL.', messages.COLOR_RED);
      } else if (error.response?.status >= 500) {
        toast.error('Server error. Please try again later.', messages.COLOR_RED);
      } else {
        toast.error(`Error ${error.response?.status || 'Unknown'}: ${error.message || 'An error occurred'}`, messages.COLOR_RED);
      }
      return false;
    } finally {
      showLoading(false);
    }
  };

  return (
    <>
      <div className="container-fluid py-3">
        {/* Header Section */}
        <HeaderInfo
          title="Block K and Block L"
          icon="fa fa-laptop"
          iconColor="#1976d2"
          titleColor="#000000"
          backgroundColor=""
          dispatchSlNo={headerData.despatchSlNo}
          permanentSlNo={headerData.permanentSlNo}
          companyName={headerData.companyName}
          companyPlaceholder="Enter Company Name"
          showBlockCType={false}
          includeTableStyles={false}
        />

        {/* Main Content Area */}
        <div className="row">
          <div className="col-md-6">
            <div className="card mb-3">
            <div className="card-header"><h5 className='mb-0 pb-1 pt-1' style={{ fontWeight: 'bold' }}> Block K: <span style={{ color: '#1976d2' }}> Information & Communication Technology (ICT) USAGE</span></h5></div>

              <div className="card-body">
                 {/* ICT Usage Section */}
              <div className="table-responsive">
                  <table className="table table-bordered mb-0">
                    <thead>
                      
                      <tr>
                        <th style={{ width: '10%' }}>SI No.</th>
                        <th style={{ width: '70%' }}>ICT Indicator</th>
                        <th style={{ width: '20%' }}>Yes-1/No-2</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ictIndicators.map((indicator, index) => (
                        <tr key={indicator.id}>
                          <td>{index + 1}</td>
                          <td className="indicator-text">
                            {indicator.question}
                          </td>
                          <td>
                            <div className="dropdown-container">
                              <select
                                className="form-select"
                                value={formData[indicator.id] || ''}
                                onChange={(e) => handleInputChange(indicator.id, e.target.value)}
                              >
                                <option value="">Select</option>
                                <option value="1">Yes</option>
                                <option value="2">No</option>
                              </select>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card mb-3">
              <div className="card-header"><h5 className='mb-0 pb-1 pt-1' style={{ fontWeight: 'bold' }}> Block L: <span style={{ color: '#1976d2' }}>Energy Conservation (EC) Measures</span></h5></div>
              <div className="card-body">
                {/* Energy Conservation Section */}
                <div className="table-responsive">
                  <table className="table table-bordered mb-0">
                    <thead>
                      
                      <tr>
                        <td className="prompt-text" colSpan="3">
                          Have any measures been taken during last financial year with regard to:-
                        </td>
                      </tr>
                      <tr>
                        <th style={{ width: '10%' }}>SI No.</th>
                        <th style={{ width: '70%' }}>EC Indicator</th>
                        <th style={{ width: '20%' }}>Yes-1/No-2</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ecIndicators.map((indicator, index) => (
                        <tr key={indicator.id}>
                          <td>{index + 1}</td>
                          <td className="indicator-text">
                            {indicator.question}
                          </td>
                          <td>
                            <div className="dropdown-container">
                              <select
                                className="form-select"
                                value={formData[indicator.id] || ''}
                                onChange={(e) => handleInputChange(indicator.id, e.target.value)}
                              >
                                <option value="">Select</option>
                                <option value="1">Yes</option>
                                <option value="2">No</option>
                              </select>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Remarks Section */}
        <div className='row'>
          <div className='col-md-12 remarks2-section'>
            <Remarks2
              remarksData={remarksData}
              setRemarksData={setRemarksData}
              blockOptions={['Block K', 'Block L']}
              itemOptions={[
                ...ictIndicators.map(ind => ind.question),
                ...ecIndicators.map(ind => ind.question)
              ]}
              remarksList={remarksList}
            />
          </div>
    </div>
    <style>{`
      .col-md-6.remarks-section {
        width: 100% !important;
      }
    `}</style>

        {/* Action Buttons */}
        <div className="row">
          <div className="col-12">
            <hr />
          </div>
          <div className="col-md-8 text-center text-md-start mb-3 mb-md-0">
            <button
              type="button"
              className="btn btn_orange mx-1"
            >
              Print
              <i className="fa fa-print ms-1"></i>
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={handleSaveToAPI}
            >
              Save
            </button>
            <button
              type="reset"
              className="btn btn_orange mx-1"
              onClick={handleReset}
            >
              Reset
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/asiScheduleMainMenu')}
            >
              Cancel
            </button>
          </div>
          <div className="col-md-4 text-center text-md-end">
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={() => navigate('/products-and-by-products')}
            >
              <i className="fa fa-angle-left me-1"></i>
              Previous
            </button>
            <button
              type="button"
              className="btn btn_orange mx-1"
              onClick={async () => {
                // Save data first, then navigate only if successful
                const saveSuccess = await handleSaveToAPI();
                if (saveSuccess) {
                  navigate('/part-a');
                }
              }}
            >
              Next
              <i className="fa fa-angle-right ms-1"></i>
            </button>
          </div>
        </div>
        {/* Filter remarks list - custom filter for Block K/L */}
        {(() => {
          let filteredRemarksList = remarksList;
          
          if (remarksList && remarksList.length > 0) {
            // Filter by Block
            if (remarksData.remarksBlock && remarksData.remarksBlock !== 'ALL' && remarksData.remarksBlock !== 'SELECT_ITEM') {
              // Extract block letter from "Block K" or "Block L" format
              let blockLetter = '';
              if (remarksData.remarksBlock) {
                // Handle formats like "Block K", "Block L", "K", "L", etc.
                const blockMatch = remarksData.remarksBlock.match(/Block\s*([KL])/i);
                if (blockMatch) {
                  blockLetter = blockMatch[1].toUpperCase();
                } else if (remarksData.remarksBlock === 'K' || remarksData.remarksBlock === 'L') {
                  blockLetter = remarksData.remarksBlock.toUpperCase();
                } else {
                  // Try to extract from full string
                  blockLetter = remarksData.remarksBlock.replace(/^BLOCK\s*[-]?\s*/i, '').trim().toUpperCase();
                }
              }
              
              filteredRemarksList = filteredRemarksList.filter(remark => {
                // Match block letter (case insensitive)
                const remarkBlock = (remark.blocks || '').toString().toUpperCase().trim();
                return remarkBlock === blockLetter || remarkBlock === remarksData.remarksBlock.toUpperCase();
              });
            }
            
            // Filter by Item (if selected)
            if (remarksData.remarksItems && remarksData.remarksItems.trim() !== '') {
              filteredRemarksList = filteredRemarksList.filter(remark => {
                const remarkItem = (remark.items || '').toString().trim();
                const selectedItem = remarksData.remarksItems.toString().trim();
                return remarkItem === selectedItem;
              });
            }
          }
          
          return <div className=''>  <Remarks__list remarksData={filteredRemarksList} /> </div>;
        })()}
      </div>
      
      <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop={false} closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover theme="light" />
    </>
  );
}

export default ICTUsageAndEnergyConservation; 